/**
 * The following schemas and utilities are used by sdk-generator to create a gen.yaml
 * config, with per-language settings. Any adjustments required to SDK generation
 * can be configured here.
 */

import yaml from 'yaml'
import { type ZodType, z } from 'zod'

import {
  docVersionSchema,
  namespaceSchema,
  slugSchema,
  zodInnerType,
} from '@/utility'

import { SdkLanguage } from './schema'

function toPascalCase(str: string): string {
  return str
    .split(/[-_]/g)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('')
}

const generationSchema = z
  .object({
    devContainers: z
      .object({ enabled: z.boolean(), schemaPath: z.string() })
      .default({ enabled: false, schemaPath: '' }),
    sdkClassName: z.string(),
    maintainOpenAPIOrder: z.boolean().default(true),
    usageSnippets: z
      .object({ optionalPropertyRendering: z.literal('withExample') })
      .default({ optionalPropertyRendering: 'withExample' }),
    useClassNamesForArrayFields: z.boolean().default(true),
    fixes: z
      .object({
        nameResolutionDec2023: z.boolean(),
        nameResolutionFeb2025: z.boolean(),
        parameterOrderingFeb2024: z.boolean(),
        requestResponseComponentNamesFeb2024: z.boolean(),
        securityFeb2025: z.boolean(),
      })
      .default({
        nameResolutionDec2023: true,
        nameResolutionFeb2025: false,
        parameterOrderingFeb2024: true,
        requestResponseComponentNamesFeb2024: true,
        securityFeb2025: true,
      }),
    auth: z
      .object({
        oAuth2ClientCredentialsEnabled: z.boolean(),
        oAuth2PasswordEnabled: z.boolean(),
      })
      .default({
        oAuth2ClientCredentialsEnabled: true,
        oAuth2PasswordEnabled: true,
      }),
  })
  .meta({ id: 'generation' })

const csharpConfigSchema = z
  .object({
    sdkClassName: z.string().default(''),
    additionalDependencies: z.array(z.string()).default([]),
    author: z.string().default('').transform(toPascalCase),
    clientServerStatusCodesAsErrors: z.boolean().default(true),
    defaultErrorName: z.string().default('APIException'),
    disableNamespacePascalCasingApr2024: z.boolean().default(true),
    dotnetVersion: z.string().default('net8.0'),
    enableSourceLink: z.boolean().default(false),
    flattenGlobalSecurity: z.boolean().default(true),
    flatteningOrder: z.enum(['parameters-first']).default('parameters-first'),
    imports: z
      .object({
        option: z.literal('openapi'),
        paths: z.object({
          callbacks: z.string().default('Models/Callbacks'),
          errors: z.string().default('Models/Errors'),
          operations: z.string().default('Models/Requests'),
          shared: z.string().default('Models/Components'),
          webhooks: z.string().default('Models/Webhooks'),
        }),
      })
      .default({
        option: 'openapi',
        paths: {
          callbacks: 'Models/Callbacks',
          errors: 'Models/Errors',
          operations: 'Models/Requests',
          shared: 'Models/Components',
          webhooks: 'Models/Webhooks',
        },
      }),
    includeDebugSymbols: z.boolean().default(false),
    inputModelSuffix: z.string().default('input'),
    maxMethodParams: z.number().default(4),
    methodArguments: z
      .enum(['infer-optional-args'])
      .default('infer-optional-args'),
    outputModelSuffix: z.string().default('output'),
    packageName: z.string(),
    packageTags: z.string().default(''),
    responseFormat: z.string().default('envelope-http'),
    sourceDirectory: z.string().default('src'),
  })
  .meta({ id: 'csharp-config' })

const goConfigSchema = z
  .object({
    sdkClassName: z.string().default(''),
    additionalDependencies: z.record(z.string(), z.string()).default({}),
    allowUnknownFieldsInWeakUnions: z.boolean().default(false),
    clientServerStatusCodesAsErrors: z.boolean().default(true),
    defaultErrorName: z.string().default('SDKError'),
    flattenGlobalSecurity: z.boolean().default(false),
    imports: z
      .object({
        option: z.literal('openapi'),
        paths: z.object({
          callbacks: z.string().default('pkg/models/callbacks'),
          errors: z.string().default('pkg/models/sdkerrors'),
          operations: z.string().default('pkg/models/operations'),
          shared: z.string().default('pkg/models/shared'),
          webhooks: z.string().default('pkg/models/webhooks'),
        }),
      })
      .default({
        option: 'openapi',
        paths: {
          callbacks: 'pkg/models/callbacks',
          errors: 'pkg/models/sdkerrors',
          operations: 'pkg/models/operations',
          shared: 'pkg/models/shared',
          webhooks: 'pkg/models/webhooks',
        },
      }),
    inputModelSuffix: z.string().default('input'),
    maxMethodParams: z.number().default(4),
    methodArguments: z
      .enum(['require-security-and-request'])
      .default('require-security-and-request'),
    outputModelSuffix: z.string().default('output'),
    packageName: z.string(),
    responseFormat: z.string().default('envelope'),
  })
  .meta({ id: 'go-config' })

const javaConfigSchema = z
  .object({
    sdkClassName: z.string().default(''),
    additionalDependencies: z.array(z.string()).default([]),
    additionalPlugins: z.array(z.string()).default([]),
    artifactID: z.string(),
    clientServerStatusCodesAsErrors: z.boolean().default(true),
    companyEmail: z.string().default('info@mycompany.com'),
    companyName: z.string().default('My Company'),
    companyURL: z.string().default('www.mycompany.com'),
    defaultErrorName: z.string().default('APIException'),
    flattenGlobalSecurity: z.boolean().default(true),
    githubURL: z.string().optional(),
    groupID: z.string(),
    imports: z
      .object({
        option: z.literal('openapi'),
        paths: z.object({
          callbacks: z.string().default('models/callbacks'),
          errors: z.string().default('models/errors'),
          operations: z.string().default('models/operations'),
          shared: z.string().default('models/components'),
          webhooks: z.string().default('models/webhooks'),
        }),
      })
      .default({
        option: 'openapi',
        paths: {
          callbacks: 'models/callbacks',
          errors: 'models/errors',
          operations: 'models/operations',
          shared: 'models/components',
          webhooks: 'models/webhooks',
        },
      }),
    inputModelSuffix: z.string().default('input'),
    license: z
      .object({
        name: z.string().default('The MIT License (MIT)'),
        shortName: z.string().default('MIT'),
        url: z.string().default('https://mit-license.org/'),
      })
      .default({
        name: 'The MIT License (MIT)',
        shortName: 'MIT',
        url: 'https://mit-license.org/',
      }),
    maxMethodParams: z.number().default(4),
    outputModelSuffix: z.string().default('output'),
    projectName: z.string(),
    templateVersion: z.string().default('v2'),
  })
  .meta({ id: 'java-config' })

const phpConfigSchema = z
  .object({
    sdkClassName: z.string().default(''),
    additionalDependencies: z
      .object({
        autoload: z.record(z.string(), z.string()),
        'autoload-dev': z.record(z.string(), z.string()),
        require: z.record(z.string(), z.string()),
        'require-dev': z.record(z.string(), z.string()),
      })
      .default({
        autoload: {},
        'autoload-dev': {},
        require: {},
        'require-dev': {},
      }),
    clientServerStatusCodesAsErrors: z.boolean().default(true),
    defaultErrorName: z.string().default('APIException'),
    flattenGlobalSecurity: z.boolean().default(true),
    imports: z
      .object({
        option: z.literal('openapi'),
        paths: z.object({
          callbacks: z.string().default('Models/Callbacks'),
          errors: z.string().default('Models/Errors'),
          operations: z.string().default('Models/Operations'),
          shared: z.string().default('Models/Components'),
          webhooks: z.string().default('Models/Webhooks'),
        }),
      })
      .default({
        option: 'openapi',
        paths: {
          callbacks: 'Models/Callbacks',
          errors: 'Models/Errors',
          operations: 'Models/Operations',
          shared: 'Models/Components',
          webhooks: 'Models/Webhooks',
        },
      }),
    inputModelSuffix: z.string().default('input'),
    laravelServiceProvider: z
      .object({ enabled: z.boolean().default(false), svcName: z.string() })
      .default({ enabled: false, svcName: '' }),
    maxMethodParams: z.number().default(4),
    methodArguments: z
      .enum(['infer-optional-args'])
      .default('infer-optional-args'),
    namespace: z.string().transform(toPascalCase),
    outputModelSuffix: z.string().default('output'),
    packageName: z.string(),
  })
  .meta({ id: 'php-config' })

const pythonConfigSchema = z
  .object({
    sdkClassName: z.string().default(''),
    additionalDependencies: z
      .object({
        dev: z.record(z.string(), z.string()),
        main: z.record(z.string(), z.string()),
      })
      .default({ dev: {}, main: {} }),
    authors: z.array(z.string()).default([]),
    clientServerStatusCodesAsErrors: z.boolean().default(true),
    defaultErrorName: z.string().default('APIError'),
    description: z.string().default(''),
    enableCustomCodeRegions: z.boolean().default(false),
    enumFormat: z.enum(['enum']).default('enum'),
    fixFlags: z
      .object({ responseRequiredSep2024: z.boolean().default(true) })
      .default({ responseRequiredSep2024: true }),
    flattenGlobalSecurity: z.boolean().default(true),
    flattenRequests: z.boolean().default(true),
    flatteningOrder: z.enum(['parameters-first']).default('parameters-first'),
    imports: z
      .object({
        option: z.literal('openapi'),
        paths: z.object({
          callbacks: z.string().default(''),
          errors: z.string().default(''),
          operations: z.string().default(''),
          shared: z.string().default(''),
          webhooks: z.string().default(''),
        }),
      })
      .default({
        option: 'openapi',
        paths: {
          callbacks: '',
          errors: '',
          operations: '',
          shared: '',
          webhooks: '',
        },
      }),
    inputModelSuffix: z.string().default('input'),
    maxMethodParams: z.number().default(999),
    methodArguments: z
      .enum(['infer-optional-args'])
      .default('infer-optional-args'),
    outputModelSuffix: z.string().default('output'),
    packageName: z.string(),
    pytestTimeout: z.number().default(0),
    responseFormat: z.enum(['flat']).default('flat'),
    templateVersion: z.string().default('v2'),
  })
  .meta({ id: 'python-config' })

const rubyConfigSchema = z
  .object({
    sdkClassName: z.string().default(''),
    additionalDependencies: z
      .object({
        development: z.record(z.string(), z.string()),
        runtime: z.record(z.string(), z.string()),
      })
      .default({ development: {}, runtime: {} }),
    author: z.string().default('').transform(toPascalCase),
    description: z.string().default(''),
    flattenGlobalSecurity: z.boolean().default(true),
    imports: z
      .object({
        option: z.literal('openapi'),
        paths: z.object({
          callbacks: z.string().default('models/callbacks'),
          errors: z.string().default('models/errors'),
          operations: z.string().default('models/operations'),
          shared: z.string().default('models/shared'),
          webhooks: z.string().default('models/webhooks'),
        }),
      })
      .default({
        option: 'openapi',
        paths: {
          callbacks: 'models/callbacks',
          errors: 'models/errors',
          operations: 'models/operations',
          shared: 'models/shared',
          webhooks: 'models/webhooks',
        },
      }),
    inputModelSuffix: z.string().default('input'),
    maxMethodParams: z.number().default(4),
    module: z.string(),
    outputModelSuffix: z.string().default('output'),
    packageName: z.string(),
  })
  .meta({ id: 'ruby-config' })

const typescriptConfigSchema = z
  .object({
    sdkClassName: z.string().default(''),
    additionalDependencies: z
      .object({
        dependencies: z.record(z.string(), z.string()),
        devDependencies: z.record(z.string(), z.string()),
        peerDependencies: z.record(z.string(), z.string()),
      })
      .default({ dependencies: {}, devDependencies: {}, peerDependencies: {} }),
    additionalPackageJSON: z.record(z.string(), z.string()).default({}),
    author: z.string().default('').transform(toPascalCase),
    clientServerStatusCodesAsErrors: z.boolean().default(true),
    defaultErrorName: z.string().default('APIError'),
    enableCustomCodeRegions: z.boolean().default(false),
    enableMCPServer: z.boolean().default(false),
    enableReactQuery: z.boolean().default(false),
    enumFormat: z.enum(['union']).default('union'),
    flattenGlobalSecurity: z.boolean().default(true),
    flatteningOrder: z.enum(['parameters-first']).default('parameters-first'),
    imports: z
      .object({
        option: z.literal('openapi'),
        paths: z.object({
          callbacks: z.string().default('models/callbacks'),
          errors: z.string().default('models/errors'),
          operations: z.string().default('models/operations'),
          shared: z.string().default('models/components'),
          webhooks: z.string().default('models/webhooks'),
        }),
      })
      .default({
        option: 'openapi',
        paths: {
          callbacks: 'models/callbacks',
          errors: 'models/errors',
          operations: 'models/operations',
          shared: 'models/components',
          webhooks: 'models/webhooks',
        },
      }),
    inputModelSuffix: z.string().default('input'),
    jsonpath: z.string().default('rfc9535'),
    maxMethodParams: z.number().default(0),
    methodArguments: z
      .enum(['require-security-and-request'])
      .default('require-security-and-request'),
    moduleFormat: z.enum(['dual']).default('dual'),
    outputModelSuffix: z.string().default('output'),
    packageName: z.string(),
    responseFormat: z.enum(['flat']).default('flat'),
    templateVersion: z.string().default('v2'),
    useIndexModules: z.boolean().default(true),
  })
  .meta({ id: 'typescript-config' })

export const sdkLanguageConfig = {
  [SdkLanguage.Go]: goConfigSchema,
  [SdkLanguage.Php]: phpConfigSchema,
  [SdkLanguage.Java]: javaConfigSchema,
  [SdkLanguage.Ruby]: rubyConfigSchema,
  [SdkLanguage.Python]: pythonConfigSchema,
  [SdkLanguage.CSharp]: csharpConfigSchema,
  [SdkLanguage.Typescript]: typescriptConfigSchema,
} satisfies Record<SdkLanguage, ZodType>

export function getSdkConfigSchema(language: SdkLanguage) {
  return z.toJSONSchema(zodInnerType(sdkLanguageConfig[language]), {
    unrepresentable: 'any',
  })
}

export function makeSdkLanguageConfig(
  language: SdkLanguage,
  metadata: {
    namespace: string
    slug: string
    version: string
    repositoryUrl?: string
    className?: string
  },
) {
  const pascalNamespace = toPascalCase(metadata.namespace)
  const pascalSlug = toPascalCase(metadata.slug)
  const sdkClassName = metadata.className || metadata.slug

  switch (language) {
    case SdkLanguage.CSharp: {
      const config: z.input<typeof csharpConfigSchema> = {
        author: pascalNamespace,
        packageName: `${pascalNamespace}.${pascalSlug}`,
        sdkClassName,
      }

      return csharpConfigSchema.parse(config)
    }
    case SdkLanguage.Go: {
      const config: z.input<typeof goConfigSchema> = {
        packageName: metadata.repositoryUrl || metadata.slug,
        sdkClassName,
      }

      return goConfigSchema.parse(config)
    }
    case SdkLanguage.Java: {
      const config: z.input<typeof javaConfigSchema> = {
        groupID: `org.${metadata.namespace}`,
        artifactID: metadata.slug,
        projectName: `${metadata.namespace}-${metadata.slug}`,
        githubURL: metadata.repositoryUrl,
        sdkClassName,
      }
      return javaConfigSchema.parse(config)
    }
    case SdkLanguage.Php: {
      const config: z.input<typeof phpConfigSchema> = {
        namespace: `${metadata.namespace}\\${pascalSlug}`,
        packageName: `${metadata.namespace}/${metadata.slug}`,
        laravelServiceProvider: {
          svcName: `${metadata.namespace}-${metadata.slug}`,
        },
        sdkClassName,
      }
      return phpConfigSchema.parse(config)
    }
    case SdkLanguage.Python: {
      const config: z.input<typeof pythonConfigSchema> = {
        packageName: `${metadata.namespace}-${metadata.slug}`,
        authors: [metadata.namespace],
        description: `Python Client SDK for ${metadata.namespace}/${metadata.slug}`,
        sdkClassName,
      }
      return pythonConfigSchema.parse(config)
    }
    case SdkLanguage.Ruby: {
      const config: z.input<typeof rubyConfigSchema> = {
        packageName: `${metadata.namespace}-${metadata.slug}`,
        module: `${pascalNamespace}${pascalSlug}`,
        author: pascalNamespace,
        description: `Ruby Client SDK for ${metadata.namespace}/${metadata.slug}`,
        sdkClassName,
      }
      return rubyConfigSchema.parse(config)
    }
    case SdkLanguage.Typescript: {
      const config: z.input<typeof typescriptConfigSchema> = {
        packageName: `@${metadata.namespace}/${metadata.slug}`,
        author: pascalNamespace,
        sdkClassName,
      }
      return typescriptConfigSchema.parse(config)
    }
  }
}

export const buildInputSchema = z
  .object({
    namespace: namespaceSchema,
    slug: slugSchema,
    version: docVersionSchema,
    language: z.enum(SdkLanguage),
    customConfig: z.string(),
    repositoryUrl: z.string().optional(),
  })
  .transform(
    ({ namespace, slug, version, language, customConfig, repositoryUrl }) => {
      const parsedConfig = sdkLanguageConfig[language].parse(
        JSON.parse(customConfig),
      )

      const generation = generationSchema.parse({
        sdkClassName: parsedConfig.sdkClassName || `${namespace}-${slug}`,
      })

      const dynamicConfig = {
        ...makeSdkLanguageConfig(language, {
          namespace,
          slug,
          version,
          repositoryUrl,
        }),
        ...parsedConfig,
        published: true,
        version,
      }

      const full = {
        configVersion: '2.0.0',
        generation,
        [language]: dynamicConfig,
      } as const

      return yaml.stringify(full)
    },
  )
  .meta({ id: 'build-input' })

/**
 * Generate gen.yaml configuration dynamically
 * for an SDK, per language
 */
export function buildSdkGenConfig(
  metadata: z.input<typeof buildInputSchema>,
): string {
  return buildInputSchema.parse(metadata)
}
